/**
 * wine-quiz.js — The Wine Engine wine quiz (variant-only).
 *
 * Parity with old main.js: start_quiz → questions → complete form → results.
 * On results / early end: Quiz Ended → What would you like to do next? (4 opts).
 */

import {
  on,
  emit,
  getState,
  setState,
  addMessage,
} from "../../../core/state.js";
import * as api from "../../../core/api.js";

const QUIZ_START_MESSAGE = "Take the wine quiz";
const QUIZ_IN_PROGRESS = ["in_progress", "completed"];
const QUIZ_START_STATUS = "start_quiz";

function quizState() {
  return getState().quiz || {};
}

function setQuizState(patch) {
  setState({ quiz: { ...quizState(), ...patch } });
}

function clearQuizState() {
  setState({ quiz: { status: "", replyType: "", isActive: false } });
  emit("ws-quiz-active", { active: false });
}

function setQuizActive(active) {
  setQuizState({ isActive: active });
  emit("ws-quiz-active", { active });
}

/** Old resolveIsClubMember — hide Build Wine Box for club members. */
async function resolveIsClubMember() {
  const customerId = getState().user?.customerId;
  if (!customerId) return false;
  try {
    const customer = await api.getCustomer(customerId);
    return customer?.is_club_member === true;
  } catch {
    return false;
  }
}

/**
 * Old createDefaultRecommendations — no quiz CTA; always Offers + Contact.
 * Shop / Build (if not club) / Offers / Contact → up to 4 chips.
 */
async function getPostQuizPromptItems() {
  const items = [
    {
      label: "Shop Our Wines",
      value: "Show me all available wines",
      icon: "bolt",
    },
  ];
  if (!(await resolveIsClubMember())) {
    items.push({
      label: "Build Wine Box",
      value: "Build a wine box",
      icon: "bolt",
    });
  }
  items.push(
    {
      label: "Check Out Our Offers",
      value: "Show me your latest offers",
      icon: "bolt",
    },
    { label: "Contact Our Team", value: "__contact__", icon: "bolt" },
  );
  return items;
}

function postDefaultOptions(items) {
  addMessage({
    role: "bot",
    kind: "text",
    data: { text: "What would you like to do next?" },
  });
  if (items?.length) {
    addMessage({ role: "bot", kind: "prompts", data: { items } });
  }
}

/**
 * Old endQuiz(): Quiz Ended badge, then ~2s later next-step prompts.
 * Used after early End Quiz / Skip (no result cards).
 */
function finishQuizUi() {
  clearQuizState();
  addMessage({
    role: "bot",
    kind: "quiz-ended",
    data: {},
  });
  schedulePostQuizOptions();
}

function schedulePostQuizOptions() {
  emit("ws-chat-typing", { active: true });
  setTimeout(async () => {
    emit("ws-chat-typing", { active: false });
    const items = await getPostQuizPromptItems();
    postDefaultOptions(items);
  }, 2000);
}

export function init({ shadow } = {}) {
  on("ws-prompt-select", ({ value, quizStatus }) => {
    if (value === QUIZ_START_MESSAGE || quizStatus === QUIZ_START_STATUS) {
      setQuizState({ status: QUIZ_START_STATUS, replyType: "start" });
    }
  });

  on("ws-chat-request", ({ text, source, quizStatus, quizReplyType }) => {
    // Prefer the status on the request itself. Do NOT flip start_quiz →
    // in_progress here — chat-handler reads quiz.state next and must still
    // send quiz_status=start_quiz (old main.js parity).
    let status = quizStatus || quizState().status;
    if (text === QUIZ_START_MESSAGE && !quizStatus) {
      status = QUIZ_START_STATUS;
      setQuizState({ status: QUIZ_START_STATUS, replyType: "start" });
    }
    const replyType = quizReplyType || quizState().replyType;

    // End Quiz only while answering questions — not on completed form / submit.
    if (status === QUIZ_START_STATUS || status === "in_progress") {
      setQuizActive(true);
    } else if (status === "completed" || text === "submitting-quiz") {
      setQuizActive(false);
    }

    if (
      (replyType === "clicked" && QUIZ_IN_PROGRESS.includes(status)) ||
      replyType === "start" ||
      text === "submitting-quiz"
    ) {
      setQuizState({ suppressUserEcho: true });
    } else if (status === "in_progress" && source === "input") {
      setQuizState({ replyType: "typed" });
    }
  });

  on("ws-quiz-response", ({ cls }) => {
    if (!cls || cls.type !== "quiz") return;

    const status = cls.quiz_status || "";
    setQuizState({ status, replyType: "" });

    if (status === "results") {
      setQuizActive(false);
      emitQuizResults(cls);
      return;
    }

    if (cls.intro_message) {
      addMessage({
        role: "bot",
        kind: "text",
        data: { text: cls.intro_message },
      });
    }

    addMessage({
      role: "bot",
      kind: "quiz-question",
      data: {
        question: cls.question || "",
        options: cls.options || [],
        showForm: status === "completed",
      },
    });

    if (status === "completed") {
      setQuizActive(false);
    } else if (status === "in_progress") {
      setQuizActive(true);
    }
  });

  on("ws-quiz-option", ({ value }) => {
    setQuizState({ replyType: "clicked", status: "in_progress" });
    emit("ws-chat-request", {
      text: value,
      source: "prompt",
      quizStatus: "in_progress",
      quizReplyType: "clicked",
      suppressUserEcho: true,
    });
  });

  on("ws-quiz-submit", ({ fullName, email }) => {
    // Hide End Quiz as soon as Submit is clicked (old endQuiz on results).
    setQuizActive(false);
    setQuizState({ replyType: "", status: "completed", isActive: false });
    emit("ws-chat-request", {
      text: "submitting-quiz",
      source: "input",
      quizStatus: "completed",
      userFullName: fullName,
      userEmail: email,
      suppressUserEcho: true,
    });
  });

  on("ws-quiz-skip", () => {
    emit("ws-quiz-end-confirmed");
  });

  on("ws-quiz-end-confirmed", () => {
    finishQuizUi();
  });

  on("ws-chat-input-submit", () => {
    const { status } = quizState();
    if (status === "completed") {
      return;
    }
    if (status === "in_progress" && shadow) {
      shadow.querySelectorAll(".ws-quiz-option-btn").forEach((btn) => {
        btn.disabled = true;
      });
    }
  });
}

function emitQuizResults(cls) {
  const results = cls.quiz_results || {};

  // Old insert order: endQuiz() first → Quiz Ended before result card /
  // description / feature wines.
  clearQuizState();
  addMessage({
    role: "bot",
    kind: "quiz-ended",
    data: {},
  });

  addMessage({
    role: "bot",
    kind: "quiz-results",
    data: { results },
  });

  if (results.introductory_message) {
    addMessage({
      role: "bot",
      kind: "text",
      data: { text: results.introductory_message },
    });
  }

  const items = (results.feature_cards || []).map((it) =>
    it?.json_data && typeof it.json_data === "object"
      ? { ...it, ...it.json_data }
      : it,
  );

  if (items.length) {
    addMessage({
      role: "bot",
      kind: "cards",
      data: {
        cardType: "wine",
        items,
        listingType: "wine_listing",
        listingMessage: "Show me all available wines",
      },
    });
    // Old createWineListing footer under quiz result wines.
    addMessage({ role: "bot", kind: "cart-actions", data: {} });
  }

  schedulePostQuizOptions();
}

export function getQuizRequestMeta() {
  const q = quizState();
  return {
    quizStatus: q.status || "",
    quizReplyType: q.replyType || "",
    suppressUserEcho: !!q.suppressUserEcho,
  };
}

export function consumeSuppressUserEcho() {
  const q = quizState();
  if (q.suppressUserEcho) {
    setQuizState({ suppressUserEcho: false });
    return true;
  }
  return false;
}

export function shouldBlockInput() {
  const { status } = quizState();
  return status === "completed";
}
